import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

/**
 * Lightweight i18n — no extra dependencies. Supports English + Urdu (RTL).
 *
 * Usage:
 *   const { t, locale, setLocale, dir } = useI18n()
 *   <p>{t('common.save')}</p>
 *
 * Adding strings: extend the catalogs in `locales.ts`. Untranslated keys
 * fall back to the key itself so missing strings are visible.
 *
 * Full translation sweep across the app is a P73-75 polish item; this
 * scaffolding lets us start adding `t(...)` calls incrementally.
 */

export type Locale = 'en' | 'ur' | 'ar'
export type Direction = 'ltr' | 'rtl'

const RTL_LOCALES: Locale[] = ['ur', 'ar']
const STORAGE_KEY = 'distroops.locale'

interface I18nContextValue {
  locale: Locale
  setLocale: (locale: Locale) => void
  dir: Direction
  t: (key: string, vars?: Record<string, string | number>) => string
}

const I18nContext = createContext<I18nContextValue | null>(null)

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template
  return template.replace(/\{(\w+)\}/g, (_, key) => String(vars[key] ?? `{${key}}`))
}

export function I18nProvider({
  catalogs,
  defaultLocale = 'en',
  children,
}: {
  catalogs: Record<Locale, Record<string, string>>
  defaultLocale?: Locale
  children: ReactNode
}) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    if (typeof window === 'undefined') return defaultLocale
    const stored = localStorage.getItem(STORAGE_KEY) as Locale | null
    return stored && stored in catalogs ? stored : defaultLocale
  })

  const dir: Direction = RTL_LOCALES.includes(locale) ? 'rtl' : 'ltr'

  // Sync <html dir> and <html lang>
  useEffect(() => {
    document.documentElement.lang = locale
    document.documentElement.dir = dir
  }, [locale, dir])

  function setLocale(next: Locale) {
    setLocaleState(next)
    try { localStorage.setItem(STORAGE_KEY, next) } catch { /* noop */ }
  }

  function t(key: string, vars?: Record<string, string | number>): string {
    const template = catalogs[locale]?.[key] ?? catalogs.en?.[key] ?? key
    return interpolate(template, vars)
  }

  return (
    <I18nContext.Provider value={{ locale, setLocale, dir, t }}>
      {children}
    </I18nContext.Provider>
  )
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext)
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>')
  return ctx
}
